import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { webNavigate, webLoadingFalse } from "../../services/navigate.service";
import { getAll, remove, PayrollPayment } from "../../services/payrollPayments.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import PlusIcon from "../../images/PlusIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import TagStatus from "../../components/TagStatus";
import { useResponsive } from "../../hook/useResponsive";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
} from "../../components/list";
import PayrollPaymentModal from "./PayrollPaymentModal";

const STATUS_DRAFT = 1;
const STATUS_COMPLETED = 2;
const STATUS_CANCELLED = 3;

const statusLabel = (statusId: number): string => {
  switch (statusId) {
    case STATUS_DRAFT:
      return "Draft";
    case STATUS_COMPLETED:
      return "Completed";
    case STATUS_CANCELLED:
      return "Cancelled";
    default:
      return "—";
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  // A date-only column: read the calendar day as written, not through a UTC Date.
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateString));
  if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}.${m}.${y}`;
};

const PayrollPayments = () => {
  const { id: routeId } = useParams<{ id?: string }>();
  const preLanguage = localStorage.getItem("language") || "en";
  const { isTouchLayout: isMobile } = useResponsive();
  const listRef = useRef<ListScreenHandle>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [modalBatchId, setModalBatchId] = useState<string | null>(null);
  const language = (window as any).languageContext?.language;
  const t = (key: string): string => language?.[key] ?? key;

  useEffect(() => {
    if (routeId) setModalBatchId(routeId);
  }, [routeId]);

  const fetchPayments = async (query: ListQuery) => {
    webLoadingFalse();
    const res = await getAll({
      page: query.page,
      pageSize: query.limit,
      statusId: query.filters.statusId ? Number(query.filters.statusId) : undefined,
    });
    const data = res?.data ?? [];
    return { rows: Array.isArray(data) ? data : [], count: Number(res?.total ?? 0) };
  };

  const isDraft = (row: PayrollPayment) => row.statusId === STATUS_DRAFT;

  // The batch opens as a modal on /payroll-payments/:id; carry the list query both ways.
  const openPayment = (id: string) =>
    webNavigate(`/${preLanguage}/payroll-payments/${id}${window.location.search}`);

  const handleDeleteClick = (id: string) => {
    setIdToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!idToDelete) return;
    setActionLoadingId(idToDelete);
    try {
      await remove(idToDelete);
      showToast({ message: t("Payroll payment deleted."), type: "success" });
      setShowDeleteModal(false);
      setIdToDelete(null);
      listRef.current?.reload();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: "Failed to delete" });
    } finally {
      setActionLoadingId(null);
    }
  };

  const inforTable = {
    title: t("No payroll payments yet"),
    path: `/${preLanguage}/payroll-payments/upload`,
    desc: t("Upload a net salary bank payment CSV to create a draft batch."),
    titleCreate: t("Upload CSV"),
  };

  const columns: ListColumn<PayrollPayment>[] = [
    {
      key: "description",
      label: "Description",
      width: 280,
      hideable: false,
      value: (p) => p.description ?? p.sourceFileName ?? "—",
      mobile: "title",
      onClick: (p) => openPayment(String(p.id)),
    },
    {
      key: "paymentDate",
      label: "Payment date",
      width: 130,
      value: (p) => formatDate(p.paymentDate),
      mobile: "subtitle",
    },
    {
      key: "period",
      label: "Period",
      width: 100,
      value: (p) => `${p.payrollPeriodYear}-${String(p.payrollPeriodMonth).padStart(2, "0")}`,
      mobile: "sheet",
    },
    {
      key: "totalAmount",
      label: "Total",
      width: 130,
      align: "right",
      render: cells.money((p) => (p.totalAmount == null ? null : Number(p.totalAmount)), true),
      mobile: "trailing",
    },
    {
      key: "lineCount",
      label: "Line count",
      width: 100,
      align: "right",
      value: (p) => String(p.lineCount ?? ""),
      mobile: "sheet",
    },
    {
      key: "statusId",
      label: "Status",
      width: 150,
      render: (p) => <TagStatus value={statusLabel(p.statusId)} label={t(statusLabel(p.statusId))} />,
      value: (p) => t(statusLabel(p.statusId)),
      mobile: "trailing",
    },
  ];

  const filters: ListFilter[] = [
    {
      key: "statusId",
      label: "Status",
      kind: "select",
      options: [
        { value: String(STATUS_DRAFT), label: t("Draft") },
        { value: String(STATUS_COMPLETED), label: t("Completed") },
        { value: String(STATUS_CANCELLED), label: t("Cancelled") },
      ],
    },
  ];

  const rowActions: ListRowAction<PayrollPayment>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: (p) => openPayment(String(p.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      hidden: (p) => !isDraft(p),
      onRun: (p) => handleDeleteClick(String(p.id)),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        overflowY: isMobile ? "auto" : undefined,
        paddingBottom: isMobile ? "24px" : undefined,
        display: isMobile ? undefined : "flex",
        flexDirection: isMobile ? undefined : "column",
      }}
    >
      <div className="lg:flex items-center justify-between">
        <p
          style={{ color: "#222525" }}
          className="text-[24px] leading-[130%] font-medium font-inter"
        >
          {t("Payroll Payments")}
        </p>
        <div
          className="flex items-center"
          style={{ gap: "16px", marginTop: isMobile ? "20px" : 0 }}
        >
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: "8px",
              padding: "8px 24px 8px 16px",
              backgroundColor: "#0F7679",
              borderRadius: "30px",
            }}
            onClick={() => webNavigate(`/${preLanguage}/payroll-payments/upload`)}
          >
            <div style={{ width: "24px", height: "24px" }} className="flex items-center justify-center">
              <PlusIcon />
            </div>
            <p style={{ color: "#FFFFFF" }} className="text-[14px] leading-[130%] font-medium font-inter">
              {t("Upload CSV")}
            </p>
          </div>
        </div>
      </div>

      <ListScreen
        ref={listRef}
        language={language}
        columns={columns}
        fetchRows={fetchPayments}
        search={false}
        filters={filters}
        rowActions={rowActions}
        countNoun="payments"
        emptyState={inforTable}
      />

      {showDeleteModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[200]"
          style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-lg font-inter mx-4"
            style={{ maxWidth: "440px", width: "100%", padding: "20px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[20px] font-semibold leading-[24px] mb-4" style={{ color: "#222525" }}>
              {t("Delete payroll payment")}
            </p>
            <p className="text-[14px] mb-6" style={{ color: "#4A5353" }}>
              {t("Are you sure you want to delete this draft? This action cannot be undone.")}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
                style={{ color: "#4A5353" }}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={actionLoadingId === idToDelete}
                className="px-4 py-2 rounded-full text-[14px] font-medium text-white"
                style={{ backgroundColor: "#BA0E13" }}
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalBatchId && (
        <PayrollPaymentModal
          batchId={modalBatchId}
          onClose={() => {
            setModalBatchId(null);
            webNavigate(`/${preLanguage}/payroll-payments${window.location.search}`);
          }}
          onSuccess={() => listRef.current?.reload()}
        />
      )}
    </div>
  );
};

PayrollPayments.displayName = "PayrollPayments";
export default PayrollPayments;
